import React, { useState, useEffect } from "react";
import { X, Sparkles, Building, PlusCircle, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../api";
import { updatePrivateLabelCustomerMappings } from "../api/costingSettingsApi";

export default function CreateNewPLCustomerModal({ isOpen, onClose, onSuccess, costingSettings }) {
  const [sourceMode, setSourceMode] = useState("new"); // "new" | "existing"
  const [erpagCustomers, setErpagCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);

  // Existing customer selection
  const [selectedErpagCustomer, setSelectedErpagCustomer] = useState("");

  // New Customer Details
  const [newCustomerName, setNewCustomerName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newTerms, setNewTerms] = useState("Net 30");
  const [newTaxId, setNewTaxId] = useState("");
  const [newAddress1, setNewAddress1] = useState("");
  const [newAddress2, setNewAddress2] = useState("");
  const [newCity, setNewCity] = useState("");
  const [newState, setNewState] = useState("");
  const [newZipcode, setNewZipcode] = useState("");
  const [newCurrency, setNewCurrency] = useState("USD");

  // Category & Packaging Mapping
  const [plCategory, setPlCategory] = useState("");
  const [packagingCategory, setPackagingCategory] = useState("Retail Pouch");
  const [formatSuffix, setFormatSuffix] = useState("CA Pouch");

  // Prefix & Override
  const [skuPrefix, setSkuPrefix] = useState("7814");
  const [packagingPrefix, setPackagingPrefix] = useState("9814");
  const [isPrefixOverridden, setIsPrefixOverridden] = useState(false);

  const [loadingPrefix, setLoadingPrefix] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchErpagCustomers();
      fetchSuggestedPrefix(packagingCategory);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isPrefixOverridden && isOpen) {
      fetchSuggestedPrefix(packagingCategory);
    }
  }, [packagingCategory]);

  const fetchErpagCustomers = async () => {
    setLoadingCustomers(true);
    try {
      const res = await api.get("/api/customers");
      if (res.data?.success && Array.isArray(res.data.data)) {
        setErpagCustomers(res.data.data);
      }
    } catch (err) {
      console.error("Error fetching ERPAG customers:", err);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchSuggestedPrefix = async (format) => {
    setLoadingPrefix(true);
    try {
      const res = await api.get(`/api/costing-settings/suggest-sku-prefix?format=${encodeURIComponent(format)}`);
      if (res.data?.success && res.data.data) {
        const { suggestedPrefix, packagingPrefix: pkgPref } = res.data.data;
        setSkuPrefix(suggestedPrefix);
        setPackagingPrefix(pkgPref);
      }
    } catch (err) {
      console.error("Error fetching suggested prefix:", err);
    } finally {
      setLoadingPrefix(false);
    }
  };

  const handlePrefixChange = (val) => {
    setSkuPrefix(val);
    setIsPrefixOverridden(true);
    if (val && val.length >= 2) {
      setPackagingPrefix(`9${val.substring(1)}`);
    }
  };

  const handleCustomerNameBlur = () => {
    if (newCustomerName.trim() && !plCategory.trim()) {
      setPlCategory(`Private Label: ${newCustomerName.trim()} Pouches`);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let finalClientName = "";
    let erpagCustomerKey = "";

    if (sourceMode === "existing") {
      if (!selectedErpagCustomer) {
        toast.error("Please select an existing ERPAG customer");
        return;
      }
      const found = erpagCustomers.find((c) => (c.customerName || c.key) === selectedErpagCustomer);
      finalClientName = found?.customerName || selectedErpagCustomer;
      erpagCustomerKey = found?.key || "";
    } else {
      if (!newCustomerName.trim()) {
        toast.error("Customer / Brand Name is required");
        return;
      }
      finalClientName = newCustomerName.trim();
    }

    if (!skuPrefix.trim()) {
      toast.error("SKU Prefix is required");
      return;
    }

    setSubmitting(true);
    try {
      // 1. Create in ERPAG if brand new
      if (sourceMode === "new") {
        try {
          const custRes = await api.post("/api/customers", {
            customerName: finalClientName,
            email: newEmail,
            termsOfPayment: newTerms,
            taxIdentificationNumber: newTaxId,
            address1: newAddress1,
            address2: newAddress2,
            city: newCity,
            state: newState,
            zipcode: newZipcode,
            currency: newCurrency,
          });
          if (custRes.data?.data?.key) {
            erpagCustomerKey = custRes.data.data.key;
          }
        } catch (custErr) {
          console.warn("Could not push customer to ERPAG API, saving locally:", custErr);
        }
      }

      // 2. Append new mapping to CostingSettings in DB
      const existingMappings = costingSettings?.privateLabelCustomerMappings || [];
      const updatedMappings = [...existingMappings];

      const categoryName = plCategory.trim() || `Private Label: ${finalClientName}`;
      const fSuffix = formatSuffix.trim() || "Pouch";
      const sPrefix = skuPrefix.trim();
      const pPrefix = packagingPrefix.trim() || `9${skuPrefix.trim().substring(1)}`;

      // Check if customer already exists in mappings
      let customerIndex = updatedMappings.findIndex(m => String(m.customerName).toLowerCase() === finalClientName.toLowerCase());

      if (customerIndex >= 0) {
        // Customer exists, merge categories
        const customer = updatedMappings[customerIndex];
        if (!customer.categories) customer.categories = [];

        let catIndex = customer.categories.findIndex(c => String(c.categoryName).toLowerCase() === categoryName.toLowerCase());
        if (catIndex >= 0) {
          // Category exists, append prefix if not exists
          const cat = customer.categories[catIndex];
          if (!cat.prefixes) cat.prefixes = [];
          const prefixExists = cat.prefixes.find(p => p.skuPrefix === sPrefix && String(p.formatSuffix).toLowerCase() === fSuffix.toLowerCase());
          if (!prefixExists) {
            cat.prefixes.push({ formatSuffix: fSuffix, skuPrefix: sPrefix, packagingPrefix: pPrefix });
          }
        } else {
          // New category
          customer.categories.push({
            categoryName,
            erpagCategoryId: "",
            prefixes: [{ formatSuffix: fSuffix, skuPrefix: sPrefix, packagingPrefix: pPrefix }]
          });
        }
      } else {
        // Brand new customer mapping
        updatedMappings.unshift({
          customerName: finalClientName,
          erpagCustomerId: erpagCustomerKey,
          categories: [
            {
              categoryName,
              erpagCategoryId: "",
              prefixes: [{ formatSuffix: fSuffix, skuPrefix: sPrefix, packagingPrefix: pPrefix }]
            }
          ],
          notes: `Created via Private Label Calculator (${sourceMode === "new" ? "New ERPAG Customer" : "Existing ERPAG Client"})`,
          updatedBy: "Calculator User"
        });
      }
      const saveRes = await updatePrivateLabelCustomerMappings(updatedMappings);

      if (saveRes?.success) {
        toast.success(`Customer "${finalClientName}" mapped with Prefix ${skuPrefix.trim()}!`);
        if (onSuccess) {
          const addedOrUpdated = customerIndex >= 0 ? updatedMappings[customerIndex] : updatedMappings[0];
          onSuccess(addedOrUpdated, saveRes.data?.privateLabelCustomerMappings || updatedMappings);
        }
        onClose();
      } else {
        toast.error("Failed to save customer mapping to settings.");
      }
    } catch (err) {
      console.error("Submit PL Customer Modal Error:", err);
      toast.error(err.message || "Failed to create customer & mapping");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl border border-gray-200 w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="px-6 py-5 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-emerald-500/20 border border-emerald-400/30 rounded-xl text-emerald-400">
              <Building className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-bold">Create New Private Label Customer</h3>
              <p className="text-xs text-gray-300">Map customer, category & SKU prefix for Private Label quoting</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 text-gray-400 hover:text-white hover:bg-white/10 rounded-xl transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Form Content */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-6 flex-1 text-sm text-gray-800">
          {/* Customer Source Selection */}
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
            <label className="block font-bold text-gray-900 mb-2">1. Select Customer Source</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setSourceMode("new")}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-semibold transition-all ${sourceMode === "new"
                    ? "bg-black text-white border-black shadow-md"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
              >
                <PlusCircle className="w-4 h-4" /> Create Brand New Customer in ERPAG
              </button>

              <button
                type="button"
                onClick={() => setSourceMode("existing")}
                className={`flex items-center justify-center gap-2 p-3 rounded-xl border text-xs font-semibold transition-all ${sourceMode === "existing"
                    ? "bg-black text-white border-black shadow-md"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                  }`}
              >
                <Building className="w-4 h-4" /> Select Existing ERPAG Customer
              </button>
            </div>
          </div>

          {/* Source Mode Fields */}
          {sourceMode === "existing" ? (
            <div>
              <label className="block font-bold text-gray-900 mb-1">Select Existing ERPAG Customer</label>
              {loadingCustomers ? (
                <div className="text-xs text-gray-400 py-2">Loading ERPAG customers...</div>
              ) : (
                <select
                  value={selectedErpagCustomer}
                  onChange={(e) => {
                    setSelectedErpagCustomer(e.target.value);
                    if (e.target.value && !plCategory) {
                      setPlCategory(`Private Label: ${e.target.value} Pouches`);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-medium"
                >
                  <option value="">-- Choose ERPAG Customer --</option>
                  {erpagCustomers.map((c) => (
                    <option key={c._id || c.key} value={c.customerName || c.key}>
                      {c.customerName || c.key} {c.email ? `(${c.email})` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block font-bold text-gray-900 mb-1">
                  Customer / Brand Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  value={newCustomerName}
                  onChange={(e) => setNewCustomerName(e.target.value)}
                  onBlur={handleCustomerNameBlur}
                  placeholder="e.g. Acme Organic Spices"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-medium"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Contact Email</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="e.g. purchasing@acme.com"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Payment Terms</label>
                <select
                  value={newTerms}
                  onChange={(e) => setNewTerms(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-medium"
                >
                  <option value="Net 30">Net 30</option>
                  <option value="Net 60">Net 60</option>
                  <option value="Due on receipt">Due on receipt</option>
                  <option value="Prepaid">Prepaid</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Currency</label>
                <select
                  value={newCurrency}
                  onChange={(e) => setNewCurrency(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-medium"
                >
                  <option value="USD">USD - US Dollar</option>
                  <option value="CAD">CAD - Canadian Dollar</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="GBP">GBP - British Pound</option>
                  <option value="AUD">AUD - Australian Dollar</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Tax ID / PIN (Optional)</label>
                <input
                  type="text"
                  value={newTaxId}
                  onChange={(e) => setNewTaxId(e.target.value)}
                  placeholder="Tax ID Number"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Address 1</label>
                <input
                  type="text"
                  value={newAddress1}
                  onChange={(e) => setNewAddress1(e.target.value)}
                  placeholder="Street Address"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Address 2</label>
                <input
                  type="text"
                  value={newAddress2}
                  onChange={(e) => setNewAddress2(e.target.value)}
                  placeholder="Apt, Suite, Unit, etc."
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">City</label>
                <input
                  type="text"
                  value={newCity}
                  onChange={(e) => setNewCity(e.target.value)}
                  placeholder="City"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Province / State</label>
                <input
                  type="text"
                  value={newState}
                  onChange={(e) => setNewState(e.target.value)}
                  placeholder="State or Province"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Zip Code</label>
                <input
                  type="text"
                  value={newZipcode}
                  onChange={(e) => setNewZipcode(e.target.value)}
                  placeholder="Zip / Postal Code"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none"
                />
              </div>
            </div>
          )}

          {/* Category & Packaging Mapping */}
          <div className="border-t border-gray-200 pt-4 space-y-4">
            <h4 className="font-bold text-gray-900 flex items-center gap-2">
              <span>2. PL Category & Packaging Setup</span>
            </h4>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block font-bold text-gray-900 mb-1">Packaging Format</label>
                <select
                  value={packagingCategory}
                  onChange={(e) => setPackagingCategory(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-medium"
                >
                  <option value="Retail Pouch">Retail Pouch</option>
                  <option value="Bulk VRAC">Bulk VRAC</option>
                  <option value="Jar / Bottle">Jar / Bottle</option>
                  <option value="Box / 6-Pack">Box / 6-Pack</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">Format / Region Suffix</label>
                <input
                  type="text"
                  value={formatSuffix}
                  onChange={(e) => setFormatSuffix(e.target.value)}
                  placeholder="e.g. CA Pouch, US Pouch, VRAC"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none font-semibold text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-900 mb-1">PL Category Name</label>
                <input
                  type="text"
                  value={plCategory}
                  onChange={(e) => setPlCategory(e.target.value)}
                  placeholder="e.g. Private Label: Acme Pouches"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-black outline-none text-xs"
                />
              </div>
            </div>
          </div>

          {/* Smart SKU Prefix Suggestion & Manual Override */}
          <div className="bg-emerald-50/70 border border-emerald-200 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-emerald-900 font-bold">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <span>3. Smart SKU Prefix Suggestion</span>
              </div>
              {isPrefixOverridden && (
                <button
                  type="button"
                  onClick={() => {
                    setIsPrefixOverridden(false);
                    fetchSuggestedPrefix(packagingCategory);
                  }}
                  className="text-xs font-semibold text-emerald-700 underline hover:text-emerald-900"
                >
                  Reset to Suggested
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">
                  Finished Goods SKU Prefix {loadingPrefix && <span className="text-gray-400 font-normal">(computing...)</span>}
                </label>
                <input
                  type="text"
                  required
                  value={skuPrefix}
                  onChange={(e) => handlePrefixChange(e.target.value)}
                  placeholder="e.g. 7814"
                  className="w-full px-3.5 py-2 border border-emerald-300 rounded-xl focus:ring-2 focus:ring-emerald-600 outline-none font-mono font-bold text-base bg-white"
                />
                <p className="text-[11px] text-emerald-700 mt-1">
                  {isPrefixOverridden ? "Manual Override Active" : "Auto-suggested based on available numbers"}
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-700 mb-1">Packaging SKU Prefix</label>
                <input
                  type="text"
                  readOnly
                  value={packagingPrefix}
                  className="w-full px-3.5 py-2 border border-emerald-200 rounded-xl font-mono font-bold text-base bg-emerald-100/60 text-emerald-800 cursor-not-allowed"
                />
                <p className="text-[11px] text-emerald-700 mt-1">Auto-generated BOM prefix (7 $\rightarrow$ 9)</p>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="pt-4 flex items-center justify-end gap-3 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-5 py-2.5 text-sm font-semibold border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="flex items-center gap-2 px-6 py-2.5 text-sm font-semibold bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors shadow-md disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              {submitting ? "Saving & Mapping..." : "Create & Map Customer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
